import { useEffect, useRef, useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { AllTypeaheadOwnAndInjectedProps, ClearButton, Typeahead as BSTypeahead } from 'react-bootstrap-typeahead'

import Icon, { EDHRECIconsType } from '@/components/Icon/Icon'
import typeaheadService from '@/services/typeahead.service'
import { formatUrl } from '@/utils/helpers'

import styles from './Typeahead.module.scss'

const filterBy = (
  option: string | { id: string; name: string },
  props: AllTypeaheadOwnAndInjectedProps<string | { id: string; name: string }>,
) => {
  const helper = (option: string) => formatUrl(option).startsWith(formatUrl(props.text))
  return typeof option === 'string' ? helper(option) : Object.values(option).some((value) => helper(value))
}

type Props = {
  className?: string
  clearButton?: boolean
  clearOnChange?: boolean
  icon?: EDHRECIconsType
  onChange: (selected: (string | { id: string; name: string; code?: string })[]) => void
  placeholder: string
  selected?: string[]
  showLoading?: boolean
  spinner?: boolean
  type?: string
  value?: string
}

const Typeahead = ({ className, clearButton, clearOnChange, icon, onChange, placeholder, selected, type }: Props) => {
  const ref = useRef<BSTypeahead<string | { id: string; name: string }>>(null)

  const [isLoading, setIsLoading] = useState(false)

  const [options, setOptions] = useState<string[] | { id: string; name: string }[]>([])

  const loadTypeahead = async () => {
    setIsLoading(true)
    if (type === 'commanders') await typeaheadService.getCommanders().then(setOptions)
    else if (type === 'partners') await typeaheadService.getPartners().then(setOptions)
    else if (type === 'sets') await typeaheadService.getSets().then(setOptions)
    else if (type === 'tags') await typeaheadService.getTags().then(setOptions)
    else await typeaheadService.getCards().then(setOptions)
    setIsLoading(false)
  }

  useEffect(() => {
    loadTypeahead()
  }, [type])

  return (
    <BSTypeahead
      inputProps={{
        autoCapitalize: 'off',
        autoCorrect: 'off',
        spellCheck: false,
      }}
      className={className}
      disabled={isLoading}
      filterBy={filterBy}
      id='searchbar'
      labelKey='name'
      minLength={['sets', 'tags'].includes(type as string) ? 0 : 3}
      multiple={['sets', 'tags'].includes(type as string)}
      onChange={(e) => {
        if (e.length > 0 && typeof e[0] === 'string' && e[0].length > 0) {
          ref.current?.blur()
        }
        if (clearOnChange) {
          ref.current?.clear()
        }
        onChange(e)
      }}
      options={options}
      placeholder={placeholder}
      ref={ref}
      selected={selected}
    >
      {({ onClear, _selected }) =>
        clearButton ? (
          <div className={styles.iconContainer}>
            <ClearButton className='color-black' onClick={onClear} />
          </div>
        ) : (
          <div className={styles.iconContainer}>
            {isLoading ? <Spinner animation='border' size='sm' /> : icon ? <Icon icon={icon} /> : null}
          </div>
        )
      }
    </BSTypeahead>
  )
}

export default Typeahead
